import { Observable, ReplaySubject } from 'rxjs';
import { ProductRelationshipProductPair } from 'src/app/models/interfaces/ProductRelationshipProductPair';
import { AttributeSettingsDTO } from '../../DTOs/AttributeSettingsDTO';
import { Attribute, MaterialChange, ModelChange, NumericChange } from '../Attribute';
import { AttributeMap, AttributePair, AttributeSetting } from '../Product';
import { ProductBlueprint } from '../ProductBlueprint';
import { AttributeInstance } from './AttributeInstances/AttributeInstance';
import { MaterialChangeInstance } from './AttributeInstances/MaterialChangeInstance';
import { ModelChangeInstance } from './AttributeInstances/ModelChangeInstance';
import { NumericChangeInstance } from './AttributeInstances/NumericChangeInstance';
import { AttributeValueOrNumber } from './AttributeValueInstances/AttributeValueInstance';

export interface AttributeWithAttributeChildren {
  attribute: Attribute;
  children: ProductRelationshipProductPair[];
}

export class AttributeSettingsController {
  private static createAttributeInstanceBasedOnAttributeValueType(attribute: Attribute) {
    if (typeof attribute === 'number') {
      return new NumericChangeInstance(attribute);
    }
    if (attribute instanceof MaterialChange) {
      return new MaterialChangeInstance(attribute);
    }
    if (attribute instanceof ModelChange) {
      return new ModelChangeInstance(attribute);
    }
    if (attribute instanceof NumericChange) {
      return new NumericChangeInstance(attribute);
    }
    return new AttributeInstance<Attribute>(attribute);
  }

  private _attributes: Map<Attribute, AttributeInstance<Attribute | ModelChange | MaterialChange>> = new Map<
    Attribute,
    AttributeInstance<Attribute | ModelChange>
  >();

  private _attributeWasSet: ReplaySubject<AttributePair> = new ReplaySubject<AttributePair>();
  public attributeWasSet$: Observable<AttributePair> = this._attributeWasSet.asObservable();

  constructor(attributes: Attribute[]) {
    for (const attribute of attributes) {
      this._attributes.set(
        attribute,
        AttributeSettingsController.createAttributeInstanceBasedOnAttributeValueType(attribute),
      );
    }
  }

  public setAttributeValueAsApplied(attribute: Attribute) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.setAttributeValueAsApplied(attribute);
    }
  }

  public checkIfAttributesAreApplied() {
    for (const [, attributeInstance] of this._attributes) {
      const isApplied = attributeInstance.checkIfAttributesAreApplied();
      if (!isApplied) return false;
    }
    return true;
  }

  public generateAttributeSettingsDTO(returnArray: AttributeSettingsDTO[] = []) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.generateAttributeSettingsDTO(returnArray);
    }
    return returnArray;
  }

  public setDefaultAttributeValues(overrideAttributes: AttributePair[]) {
    for (const [attribute, attributeInstance] of this._attributes) {
      let overriden = false;
      for (const attributePair of overrideAttributes) {
        if (attribute === attributePair.attribute) {
          const success$ = attributeInstance.setAttributeValue(
            attributePair.attributeValue,
            attributePair.priority ? attributePair.priority : 0,
          );

          if (success$) {
            this._attributeWasSet.next(attributePair);
            if (success$ instanceof Observable) success$.subscribe(this._attributeWasSet);
          }
          overriden = true;
        }
      }
      if (!overriden) {
        const defaultValue =
          attribute.defaultValue !== undefined && attribute.defaultValue !== null
            ? attribute.defaultValue
            : attribute.attributeValues[0];
        const success$ = attributeInstance.setAttributeValue(defaultValue, 0);
        if (success$) {
          this._attributeWasSet.next({ attribute: attribute, attributeValue: defaultValue });
          if (success$ instanceof Observable) success$.subscribe(this._attributeWasSet);
        }
      }
      attributeInstance.setDefaultAttributeValues(overrideAttributes);
    }
  }

  public setAttribute(attribute: Attribute, attributeValue: AttributeValueOrNumber, priority: number): boolean {
    const attributeInstance = this._attributes.get(attribute);
    if (attributeInstance) {
      const success$ = attributeInstance.setAttributeValue(attributeValue, priority);
      if (success$) {
        this._attributeWasSet.next({ attribute: attribute, attributeValue: attributeValue });
        if (success$ instanceof Observable) {
          success$.subscribe(this._attributeWasSet);
        }
        return true;
      }
    }

    let successfulySetAttribute = false;
    for (const [, attributeInstance] of this._attributes) {
      successfulySetAttribute = attributeInstance.setAttribute(attribute, attributeValue, priority);
    }
    return successfulySetAttribute;
  }

  public reApplyAttributes() {
    for (const [attribute, attributeInstance] of this._attributes) {
      attributeInstance.reApplyAttributes();
      if (attributeInstance.attributeValue)
        this._attributeWasSet.next({ attribute: attribute, attributeValue: attributeInstance.attributeValue });
    }
  }

  //when a modelattribute is a child of an attribute, it can become an "orphan" when the parent changes. This function finds the orphans so they can be removed
  protected checkIfAttributeChildrenAreStillSet() {}

  /* public getAttributeChildren(attribute: Attribute, attributeChildren: ProductRelationshipProductPair[] = []) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.getAttributeChildren(attribute, attributeChildren);
    }
    return attributeChildren;
  }

  public getAllAttributeChildren(attributeWithAttributeChildren: AttributeWithAttributeChildren[] = []) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.getAllAttributeChildren(attributeWithAttributeChildren);
    }
    return attributeWithAttributeChildren;
  } */

  public generateAttributePairs(attributePairs: AttributePair[] = []) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.generateAttributePairs(attributePairs);
    }
    return attributePairs;
  }

  public getAttributeValueFromAttribute(attribute: Attribute): AttributeValueOrNumber | undefined {
    const attributeInstance = this._attributes.get(attribute);
    if (attributeInstance) return attributeInstance.attributeValue;
    for (const [, attributeInstance] of this._attributes) {
      const foundAttribute = attributeInstance.getAttributeValueFromAttribute(attribute);
      if (foundAttribute) return foundAttribute;
    }
    return undefined;
  }

  public referenceAttributesCanBeAppliedToProductBlueprint(productBlueprint: ProductBlueprint) {
    for (const [, attributeInstance] of this._attributes) {
      if (attributeInstance.referenceAttributesCanBeAppliedToProductBlueprint(productBlueprint)) return true;
    }
    return false;
  }

  public findGlobalAttributes(attributeInstances: AttributeInstance<Attribute>[] = []) {
    for (const [, attributeInstance] of this._attributes) {
      attributeInstance.findGlobalAttributes(attributeInstances);
    }
    return attributeInstances;
  }

  public generateAttributeMap() {
    const attributeMap: AttributeMap = new Map<Attribute, AttributeSetting | undefined>();
    for (const [attribute, attributeInstance] of this._attributes) {
      attributeMap.set(attribute, attributeInstance.generateAttributeSetting());
    }
    return attributeMap;
  }

  //TODO: Zrihtaj disposanje
  public dispose() {
    //this._attributeWasSet.complete();
  }
}
